import { computedFrom } from 'aurelia-framework';
import { find, isNaN, isEmpty, kebabCase, includes } from 'lodash';

export enum NodeSelectedStatus {
	Selected = 1,
	Indeterminate = -1,
	NotSelected = 0
}

export class NodeDraggingModel {
	private valuedPrimitiveTypes = ["number", "string", "boolean"];

	id: string;
	title: string;
	parentName: string;
	parentId: string;
	isExpanded: boolean;
	isHidden: boolean;
	sortOrder: number;
	displayText: string;
	level: number;
	nodeType: string;

	constructor(node: NodeModel) {
		for(const i in node) {
			const prop = node[i];
			if(includes(this.valuedPrimitiveTypes, typeof prop)) {
				this[i] = prop;
			}
		}
	}
}

export class NodeType {
	static CostCenter = 'CostCenter';
	static OrganizationalUnit = 'OrganizationalUnit';
	static Service = 'Service';
}

export class NodeModel {
	id: string;
	title: string;
	children: NodeModel[];
	parent: NodeModel;
	parentName: string;
	parentId: string;
	isHidden: boolean;
	sortOrder: number;
	displayText: string;
	level: number;
	nodeType: string;	
	hasSubViewContent: boolean = true; // Settable by the TreeNode's sub-view template's content
	code: string;

	private nodePathSegments: string[];
	private customCssClasses = '';

	// Meta information parsed from structure import
	private nodeDefaultDataItems: string[] | undefined;
	private nodeExtraFields: string[] | undefined;

	constructor(
		title: string,
		sortOrder: number = 1,
		parentName: string = null,
		id: string = null,
		code: string = null) {

		this.id = id;
		this.title = title;
		this.parentName = parentName;
		this.children = [];
		this.displayText = null;
		this.level = 0;
		this.nodeType = null;
		this.parent = null;
		this.code = code;

		if (!isNaN(sortOrder)) {
			this.sortOrder = sortOrder;
		}
	}

	@computedFrom('nodeType')
	get nodeTypeCssClass(): string {
		return kebabCase(this.nodeType);
	}

	toggleCssClass(className: string) {
		const segments = this.customCssClasses.split(' ');
		if(segments.length && includes(segments, className)) {
			segments.splice(segments.indexOf(className), 1);
		} else {
			segments.push(className);
		}
		this.customCssClasses = segments.join(' ');
	}

	get pathSegments(): string[] {
		if (!this.nodePathSegments) {
			this.nodePathSegments = this.nodePath();
		}
		return this.nodePathSegments;
	}

	get defaultDataItems(): string[] {
		return this.nodeDefaultDataItems;
	}

	set defaultDataItems(dataItems: string[]) {
		if (dataItems.length === 0) {
			return;
		} 

		this.nodeDefaultDataItems = dataItems;
	}

	get extraFields(): string[] {
		return this.nodeExtraFields;
	}

	set extraFields(extraFields: string[]) {
		if (extraFields.length === 0) {
			return;
		}

		this.nodeExtraFields = extraFields;
	}

	traverseTree(action: (node: NodeModel) => void): void {
		action(this);

		if (this.children.length > 0) {
			this.children.map(o => o.traverseTree(action));
		}
	}

	traverseParents(
		action: (node: NodeModel, childNode: NodeModel, isRoot: boolean) => void,
		childNode: NodeModel = null): void {

		let isRoot = false;
		if (this.parent) {
			this.parent.traverseParents(action, this);
		} else {
			isRoot = true;
		}
		action(this, childNode, isRoot);
	}

	nodePath(): string[] {
		const path = [];
		this.traverseParents((current: NodeModel) => path.push(current.title));
		return path;
	}

	static createNew(node: NodeModel): NodeModel {
		const model = new NodeModel(node.title, node.sortOrder, node.parentName, node.id);
		model.level = node.level;
		model.nodeType = node.nodeType;
		return model;
	}

	// Rebuild the children array to force re-evaluation of computed bindings
	refreshChildren() {
		this.children = [].concat(this.children);
	}
}

export function findTreenodeById<T extends NodeModel>(tree: T[], id: string) {
	let result: T = null;

	for (const rootNode of tree) {

		rootNode.traverseTree(node => {
			if (node.id === id) {
				result = node as T;
			}
		});

		if (result != null) { return result; } // shortcut, if result was found
	}

	return result;
}

export function flattenTree(tree: NodeModel[]): NodeModel[] {
	const flattened = [];
	const action = (current: NodeModel) => {
		if (isEmpty(current)) { return; }
		const model = new NodeModel(current.title, current.sortOrder);
		model.level = current.level;
		model.parent = current.parent;
		model.id = current.id;
		model.nodeType = current.nodeType;
		flattened.push(model);
	};
	tree.map(current => current.traverseTree(action));
	return flattened;
}

export function createTree(nodes: any[], parent: any, customMapping: (dto, node) => void = null) {
	return nodes.map(dto => {
		const node = new NodeModel(dto.title, dto.sortOrder, parent ? parent.title : null, dto.id);

		if (customMapping) {
			customMapping(dto, node);
		}

		node.parent = parent;
		node.level = dto.level;
		node.nodeType = dto.nodeType;
		node.children = createTree(dto.children, node, customMapping);

		return node;
	});
}

export function concatChildren(to: NodeModel, from: NodeModel) {
	if (from.children) {
		return;
	}
	const concatenate = node => {
		const inArrayObject = find(to.children, n => n.id === n.id);
		if (!inArrayObject) {
			to.children.push(node);
		} else {
			concatChildren(inArrayObject, node);
		}
	};
	from.children.forEach(concatenate);
}
